import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, X } from "lucide-react";

/**
 * Weekly Timothy check-in card. Shows only when the user has signed the covenant
 * and has not yet answered this ISO week.
 */
export default function TimothyCard() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [note, setNote] = useState("");
  const [nameField, setNameField] = useState("");
  const [dismissed, setDismissed] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/timothy/state");
      setState(data);
      setNameField(data.timothy_name || "");
    } catch (e) {
      console.error("timothy/state failed", e);
    }
  };
  useEffect(() => { load(); }, [user?.covenant_signed]);

  if (!state?.eligible || state.answered_this_week || dismissed) return null;

  const answer = async (invited) => {
    if (!state.timothy_name && nameField.trim()) {
      await api.patch("/settings", { timothy_name: nameField.trim() });
    }
    await api.post("/timothy/checkin", { invited, note });
    toast.success(invited ? "So good. Keep the door open." : "Kept honest. Try again this week.");
    load();
    setNote("");
  };

  return (
    <section className="border border-primary/30 bg-primary/[0.04] p-6 md:p-8 relative" data-testid="timothy-card">
      <button onClick={() => setDismissed(true)} data-testid="timothy-dismiss"
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground" aria-label="Dismiss until next visit">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-3">
        <Sparkles className="w-4 h-4 text-primary" />
        <p className="text-xs uppercase tracking-[0.2em] text-primary">Your Timothy · Weekly check-in</p>
      </div>
      <h2 className="font-serif text-3xl mt-3 leading-tight">
        {state.timothy_name
          ? <>Have you invited <em>{state.timothy_name}</em> into the handbook this week?</>
          : "Who is your Timothy — the one you'll walk through this next?"}
      </h2>

      {!state.timothy_name && (
        <div className="mt-6">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Their name</label>
          <input value={nameField} onChange={(e) => setNameField(e.target.value)} data-testid="timothy-name-input"
            className="notebook-input w-full max-w-sm mt-1 text-lg" placeholder="e.g. Sam, my brother" />
        </div>
      )}

      <div className="mt-5">
        <label className="text-xs uppercase tracking-widest text-muted-foreground">A quick word (optional)</label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="timothy-note"
          rows={2} placeholder="How the conversation went, or what you're waiting for…"
          className="bg-transparent border-0 border-b rounded-none focus-visible:ring-0 focus-visible:border-primary" />
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button onClick={() => answer(true)} data-testid="timothy-yes" className="rounded-full">
          Yes — I invited them
        </Button>
        <Button onClick={() => answer(false)} variant="outline" data-testid="timothy-no" className="rounded-full">
          Not yet — remind me
        </Button>
      </div>
    </section>
  );
}
